import { useRef } from 'react';
import Button from '@/components/base/Button';
import MarketSelect from '@/components/base/MarketSelect';
import Switch from '@/components/base/Switch';
import { currenciesForMarkets } from '@/utils/markets';
import BriefAttachments from './BriefAttachments';
import { contentTypeOptions, type CampaignBrief, type CampaignAttachment } from '../types';

const baseCurrencies = ['KES', 'TZS', 'UGX', 'USD'];

interface BriefFormProps {
  brief: CampaignBrief;
  onChange: (patch: Partial<CampaignBrief>) => void;
  onGenerate: () => void;
  onUseExample: () => void;
  attachments: CampaignAttachment[];
  onAttachmentsChange: (attachments: CampaignAttachment[]) => void;
}

function Field({ label, hint, children }: { label: React.ReactNode; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-xs font-label font-medium text-foreground-700">
        {label}
      </span>
      {children}
      {hint ? <span className="mt-1 block text-[11px] text-foreground-400">{hint}</span> : null}
    </label>
  );
}

const inputClass =
  'w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30';

/** A suggested deliverable line for each content format — dropped in when a format is picked. */
const deliverableSuggestions: Record<string, string> = {
  'instagram-post': '1× Instagram feed post',
  'instagram-reel': '1× Instagram Reel (60s min)',
  'instagram-story': '1× Instagram Story with swipe-up link',
  'tiktok-video': '1× TikTok video',
  'youtube-video': '1× YouTube video integration',
  'youtube-shorts': '1× YouTube Short',
  'x-post': '1× X post',
  'facebook-post': '1× Facebook post',
  'linkedin-post': '1× LinkedIn post',
  'blog-post': '1× Blog post',
  'podcast-mention': '1× Podcast mention',
  'event-appearance': '1× Event appearance (in-person)',
};

export default function BriefForm({
  brief,
  onChange,
  onGenerate,
  onUseExample,
  attachments,
  onAttachmentsChange,
}: BriefFormProps) {
  // Paste a brief fills the description but never the objective (production's extraction has none).
  const canGenerate = Boolean(brief.objective.trim() || brief.description.trim());
  // Tracks the last custom "Other" text so its auto-added deliverable line can
  // be updated in place as the user edits it.
  const lastCustomRef = useRef('');

  const splitDeliverables = (value: string) =>
    value
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);

  const toggle = (key: 'contentTypes', value: string) => {
    const current = brief[key];
    const removing = current.includes(value);
    const nextTypes = removing
      ? current.filter((item) => item !== value)
      : [...current, value];
    const patch: Partial<CampaignBrief> = { [key]: nextTypes } as Partial<CampaignBrief>;

    // Keep the deliverables list in step with the content formats picked —
    // add the suggested line on select, remove it on deselect. Lines the user
    // has edited by hand are left untouched.
    // The custom "Other" format mirrors whatever text the user typed, so its
    // line is derived from customContentType instead of a preset suggestion.
    const customLine =
      value === 'other' && brief.customContentType.trim()
        ? `1× ${brief.customContentType.trim()}`
        : undefined;
    const lineToSync = deliverableSuggestions[value] ?? customLine;
    if (lineToSync) {
      const lines = splitDeliverables(brief.deliverables);
      if (removing) {
        patch.deliverables = lines.filter((line) => line !== lineToSync).join('\n');
      } else if (!lines.includes(lineToSync)) {
        patch.deliverables = [...lines, lineToSync].join('\n');
      }
    }

    onChange(patch);
  };

  // When the custom "Other" text changes, swap the previous auto-added line for
  // the new one so the deliverables list always reflects what was typed.
  const updateCustomContentType = (nextValue: string) => {
    const prevText = lastCustomRef.current.trim();
    const nextText = nextValue.trim();
    const prevLine = prevText ? `1× ${prevText}` : '';
    const nextLine = nextText ? `1× ${nextText}` : '';

    let lines = splitDeliverables(brief.deliverables);
    if (prevLine && prevLine !== nextLine) {
      lines = lines.filter((line) => line !== prevLine);
    }
    if (nextLine && !lines.includes(nextLine)) {
      lines = [...lines, nextLine];
    }

    lastCustomRef.current = nextValue;
    onChange({ customContentType: nextValue, deliverables: lines.join('\n') });
  };

  const today = (() => {
    const now = new Date();
    const offset = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - offset).toISOString().slice(0, 10);
  })();

  const marketCurrencies = currenciesForMarkets(brief.markets);
  const currencyOptions = Array.from(
    new Set([...(marketCurrencies.length > 0 ? marketCurrencies : baseCurrencies), brief.currency]),
  );

  return (
    <div className="rounded-lg border border-background-200 bg-background-50">
      <div className="flex flex-col gap-2 border-b border-background-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 text-background-50 text-base">
            <i className="ri-sparkling-2-line" />
          </span>
          <div>
            <h2 className="font-heading text-sm font-semibold text-foreground-950">
              Campaign brief
            </h2>
            <p className="text-xs text-foreground-500">
              Describe the objective — CPaaS infers the creator profile.
            </p>
          </div>
        </div>
        <Button variant="ghost" size="sm" icon="ri-magic-line" onClick={onUseExample}>
          Use an example
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
        <Field label="Campaign name">
          <input
            type="text"
            value={brief.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="e.g. Ubuntu Naturals SPF Launch"
            className={inputClass}
          />
        </Field>
        <Field label="Brand / client">
          <input
            type="text"
            value={brief.brandClient}
            onChange={(e) => onChange({ brandClient: e.target.value })}
            placeholder="e.g. Ubuntu Naturals"
            className={inputClass}
          />
        </Field>

        <div className="md:col-span-2">
          <Field
            label="Campaign Objective"
            hint="Plain language — the AI reads this to rank creators."
          >
            <textarea
              value={brief.objective}
              onChange={(e) => onChange({ objective: e.target.value })}
              rows={3}
              maxLength={500}
              placeholder="What should this campaign achieve, for whom, and in which markets?"
              className={`${inputClass} resize-none`}
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field
            label="Campaign Description"
            hint="This is shared with the creators you invite."
          >
            <textarea
              value={brief.description}
              onChange={(e) => onChange({ description: e.target.value })}
              rows={3}
              maxLength={500}
              placeholder="Describe the product and key messages for the creators you invite…"
              className={`${inputClass} resize-none`}
            />
            <span className="mt-1 block text-right text-[11px] text-foreground-400">
              {brief.description.length}/500
            </span>
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field
            label={
              <span>
                Content type{' '}
                <span className="font-normal text-foreground-400">(select all that apply)</span>
              </span>
            }
          >
            <div className="flex flex-wrap gap-1.5">
              {contentTypeOptions.map((type) => {
                const active = brief.contentTypes.includes(type.id);
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => toggle('contentTypes', type.id)}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-label transition-colors cursor-pointer ${
                      active
                        ? 'border-secondary-500 bg-secondary-100 text-secondary-900'
                        : 'border-background-300 text-foreground-600 hover:border-secondary-300'
                    }`}
                  >
                    <span aria-hidden="true">{type.emoji}</span>
                    {type.id === 'other' && brief.customContentType.trim()
                      ? brief.customContentType.trim()
                      : type.label}
                  </button>
                );
              })}
            </div>
            {brief.contentTypes.includes('other') ? (
              <div className="mt-3">
                <input
                  type="text"
                  value={brief.customContentType}
                  onChange={(e) => updateCustomContentType(e.target.value)}
                  maxLength={60}
                  placeholder="Describe the content type — e.g. Newsletter feature, Live stream"
                  className={inputClass}
                  aria-label="Custom content type"
                />
                <span className="mt-1 block text-[11px] text-foreground-400">
                  Tell CPaaS exactly what you need — creators will be briefed on this format.
                </span>
              </div>
            ) : null}
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field
            label="Deliverables"
            hint="Exactly what content creators are expected to produce for this campaign."
          >
            <textarea
              value={brief.deliverables}
              onChange={(e) => onChange({ deliverables: e.target.value })}
              rows={4}
              maxLength={500}
              placeholder={'e.g.\n1× Instagram Reel (60s min)\n3× Stories with swipe-up link\n1× Feed post tagging @brandaccount'}
              className={`${inputClass} resize-none`}
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <Field
            label="Markets"
            hint="Start typing to search all African countries — select as many as you need."
          >
            <MarketSelect
              value={brief.markets}
              onChange={(value) => {
                const patch: Partial<CampaignBrief> = { markets: value };
                const single = value.length === 1 ? currenciesForMarkets(value)[0] : undefined;
                if (single) patch.currency = single;
                onChange(patch);
              }}
              placeholder="Search African countries…"
            />
          </Field>
        </div>

        <Field
          label="Overall budget"
          hint="Total budget for the campaign. You can fine-tune the amount for each creator after you select them."
        >
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              value={brief.budget || ''}
              onChange={(e) => onChange({ budget: Number(e.target.value) })}
              placeholder="1500000"
              className={inputClass}
            />
            <div className="relative shrink-0">
              <select
                value={brief.currency}
                onChange={(e) => onChange({ currency: e.target.value })}
                className="h-[38px] cursor-pointer appearance-none rounded-md border border-background-300 bg-background-50 pl-3 pr-8 text-sm font-label text-foreground-700 focus:border-primary-400 focus:outline-none"
              >
                {currencyOptions.map((currency) => (
                  <option key={currency} value={currency}>
                    {currency}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-500" />
            </div>
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Start date" hint="Must be today or a future date.">
            <input
              type="date"
              value={brief.startDate}
              min={today}
              onChange={(e) => {
                let nextStart = e.target.value;
                if (nextStart && nextStart < today) {
                  nextStart = today;
                }
                const patch: Partial<CampaignBrief> = { startDate: nextStart };
                if (brief.endDate && nextStart && brief.endDate < nextStart) {
                  patch.endDate = nextStart;
                }
                onChange(patch);
              }}
              className={inputClass}
            />
          </Field>
          <Field
            label="End date"
            hint="Must be a future date, and cannot be earlier than the start date."
          >
            <input
              type="date"
              value={brief.endDate}
              min={brief.startDate && brief.startDate > today ? brief.startDate : today}
              onChange={(e) => {
                const floor =
                  brief.startDate && brief.startDate > today ? brief.startDate : today;
                let nextEnd = e.target.value;
                if (nextEnd && nextEnd < floor) {
                  nextEnd = floor;
                }
                onChange({ endDate: nextEnd });
              }}
              className={inputClass}
            />
          </Field>
        </div>

        <div className="md:col-span-2">
          <div className="flex items-start justify-between gap-4 rounded-md border border-background-200 bg-background-100 px-4 py-3">
            <div className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-700 text-base">
                <i className={brief.inviteOnly ? 'ri-lock-line' : 'ri-group-line'} />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-800">Invite Only</p>
                <p className="mt-0.5 text-xs leading-relaxed text-foreground-500">
                  {brief.inviteOnly
                    ? 'Only creators you invite can join this campaign.'
                    : 'Creators who match this brief can discover the campaign and apply — it is only shown to relevant creators, not the whole network.'}
                </p>
              </div>
            </div>
            <Switch
              checked={brief.inviteOnly}
              onChange={(value) => onChange({ inviteOnly: value })}
              label="Invite only"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-background-200 px-5 py-5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-accent-500 text-background-50 text-base">
            <i className="ri-link" />
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-950">
            Onboarding Link
          </h3>
        </div>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="w-full sm:max-w-xs">
            <Field
              label={
                <span>
                  Link expires on{' '}
                  <span className="font-normal text-foreground-400">(optional)</span>
                </span>
              }
            >
              <input
                type="date"
                value={brief.linkExpiresOn}
                min={today}
                onChange={(e) => {
                  let next = e.target.value;
                  if (next && next < today) {
                    next = today;
                  }
                  onChange({ linkExpiresOn: next });
                }}
                className={inputClass}
              />
            </Field>
          </div>
          <p className="flex items-center gap-1.5 text-xs text-foreground-500 sm:pb-2">
            <i className="ri-information-line text-accent-600" />
            Works for creators outside Twiva — leave blank for no expiry, or pick a future date.
          </p>
        </div>
      </div>

      <BriefAttachments attachments={attachments} onChange={onAttachmentsChange} />

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-background-200 px-5 py-4">
        <p className="text-xs text-foreground-500">
          {canGenerate
            ? 'CPaaS will match this brief against the Twiva creator network.'
            : 'Add a campaign objective or description to match creators.'}
        </p>
        <Button
          icon="ri-sparkling-2-line"
          onClick={onGenerate}
          disabled={!canGenerate}
        >
          Generate recommendations
        </Button>
      </div>
    </div>
  );
}